/**
 * Mirrors `backend/src/config/permissions.ts`.
 *
 * This enum used to declare an `editor` role the API has never accepted and to
 * omit `instructor`, which the API has always accepted — so the admin role
 * picker, built from `Object.values(UserRole)`, offered one option that always
 * failed and hid one that worked. The values below are the real ones, and
 * `useRoleCatalogue()` fetches the authoritative list at runtime so this cannot
 * drift again unnoticed.
 */
export enum UserRole {
  READER = "reader",
  INSTRUCTOR = "instructor",
  CONTENT_MANAGER = "contentManager",
  MARKETING = "marketing",
  SOCIAL_MEDIA = "socialMedia",
  ADMISSIONS = "admissions",
  FINANCE = "finance",
  SUPPORT = "support",
  AUDITOR = "auditor",
  ADMIN = "admin",
  SUPER_ADMIN = "superAdmin",
}

/**
 * A capability, not a job title. The UI asks "may this person issue a
 * certificate?", never "is this person an admin?" — the second question stopped
 * having a useful answer the moment there was more than one kind of staff.
 */
export type Permission = string;

/** What `GET /api/users/me/permissions` returns. */
export interface MyPermissions {
  roles: string[];
  primaryRole: string;
  permissions: Permission[];
}

/** One entry from `GET /api/users/roles`. */
export interface RoleDefinition {
  name: string;
  description: string;
  permissions: Permission[];
  /** False when the signed-in user is not allowed to hand this role out. */
  assignable: boolean;
}

/**
 * The permission strings the frontend gates on. Not exhaustive — only the ones
 * some component actually branches on.
 */
export const PERMISSIONS = {
  COURSE_WRITE: "course:write",
  /** Every course opens without an enrolment. Held by admin and superAdmin. */
  COURSE_ACCESS_ALL: "course:access:all",
  QUIZ_WRITE: "quiz:write",
  INSTRUCTOR_WRITE: "instructor:write",
  BLOG_WRITE: "blog:write",
  /** Editing the standing pages — terms, privacy. Wider blast radius than a post. */
  PAGE_WRITE: "page:write",
  /** Hiding and restoring readers' comments. Split out of BLOG_WRITE. */
  COMMENT_MODERATE: "comment:moderate",
  /** Browsing the media library. Adding to it is UPLOAD_WRITE. */
  MEDIA_READ: "media:read",
  UPLOAD_WRITE: "upload:write",
  UPLOAD_DELETE: "upload:delete",
  BANNER_WRITE: "banner:write",
  EVENT_WRITE: "event:write",
  TEAM_WRITE: "team:write",
  TESTIMONIAL_WRITE: "testimonial:write",
  PITCHDECK_WRITE: "pitchdeck:write",
  NOTIFICATION_SEND: "notification:send",
  NEWSLETTER_READ: "newsletter:read",
  /** Composing and sending. Narrower than NEWSLETTER_READ — a send cannot be undone. */
  NEWSLETTER_SEND: "newsletter:send",
  CONTENT_CALENDAR_READ: "content:calendar:read",
  /** Logging that a post went out. The only write a socialMedia account holds. */
  CONTENT_CALENDAR_PUBLISH: "content:calendar:publish",
  /** Editing the caption and hashtags a post ships with. */
  CONTENT_CALENDAR_WRITE: "content:calendar:write",
  /** The free-resource lead table. Personal data, so it is read-only and gated. */
  LEAD_READ: "lead:read",
  /**
   * Changing a nurture sequence: stopping one, forcing a scheduler pass, sending
   * a preview, or recording a WhatsApp message as sent by hand. Split from
   * LEAD_READ for the same reason NEWSLETTER_SEND is split from NEWSLETTER_READ.
   */
  NURTURE_WRITE: "nurture:write",
  /**
   * The cold-calling console. Three permissions, mirroring the split in
   * `backend/src/config/permissions.ts`: working the queue, recording what was
   * said, and running the groups. READ and WRITE are split for the same reason
   * NURTURE_WRITE is split from LEAD_READ — a disposition changes a person's
   * status and can stop their emails, which reading a briefing cannot.
   */
  CALL_DESK_READ: "calldesk:read",
  CALL_DESK_WRITE: "calldesk:write",
  /** Creating groups, assigning contacts, seeing everyone's activity. */
  CALL_DESK_MANAGE: "calldesk:manage",
  SCHOLARSHIP_WRITE: "scholarship:write",
  APPLICATION_READ: "application:read",
  /**
   * The course conversion funnel. Mirrors CONVERSION_READ in
   * backend/src/config/permissions.ts, which is the authority — this copy only
   * decides what the admin shell renders, never what the API allows.
   */
  CONVERSION_READ: "conversion:read",
  /**
   * The paid-advertising dashboard: spend beside revenue, which is
   * finance-shaped data. Marketing and finance hold it.
   */
  PAID_ADS_READ: "paidads:read",
  /**
   * Importing a spend file, or undoing that import. Split from the read because
   * a bad import breaks nothing visibly — it silently changes what every
   * campaign on the page appears to be worth.
   */
  PAID_ADS_WRITE: "paidads:write",
  /**
   * The partner programme. Three permissions, mirroring the split in
   * `backend/src/config/permissions.ts`: reading the roster and the ledger,
   * running the programme, and sending money — which only `finance` and the two
   * admin roles hold, so recruiting a partner and paying one take two people.
   */
  AFFILIATE_READ: "affiliate:read",
  AFFILIATE_WRITE: "affiliate:write",
  AFFILIATE_PAYOUT: "affiliate:payout",
  REVENUE_READ: "revenue:read",
  PROMO_READ: "promo:read",
  PROMO_WRITE: "promo:write",
  /**
   * Personal offers are their own pair, so marketing can run them without
   * holding the catalogue-wide promo codes. See config/permissions.ts.
   */
  OFFER_READ: "offer:read",
  /** Everyone's offers, not just your own. Ends in `:read` so the auditor inherits it. */
  OFFER_ALL_READ: "offer:all:read",
  OFFER_WRITE: "offer:write",
  /** Reading the shared WhatsApp inbox — private customer conversations. */
  WHATSAPP_INBOX_READ: "whatsapp:read",
  /** Replying as the business. The message records which account sent it. */
  WHATSAPP_INBOX_WRITE: "whatsapp:write",
  /** Pairing or unlinking the account itself. Admin roles only — see permissions.ts. */
  WHATSAPP_INBOX_MANAGE: "whatsapp:manage",
  USER_READ: "user:read",
  USER_SUSPEND: "user:suspend",
  USER_ROLE_WRITE: "user:role:write",
  /**
   * The enrolment register: who took which seat, and whether they ever opened a
   * lesson. Read-only and its own permission, so support and marketing can see
   * the register without holding ENROLLMENT_WRITE, which grants and revokes
   * seats. The `:read` suffix sweeps it into `auditor` — see
   * `backend/src/config/permissions.ts`, which is the authority.
   */
  ENROLLMENT_READ: "enrollment:read",
  ENROLLMENT_WRITE: "enrollment:write",
  CERTIFICATE_READ: "certificate:read",
  CERTIFICATE_ISSUE: "certificate:issue",
  ACTIVITY_READ: "activity:read",
  STATS_READ: "stats:read",
  ADMIN_PANEL: "admin:panel",
} as const;

export interface UserProfile {
  _id: string;
  displayName?: string;
  email?: string;
  /**
   * Returned by login, register and google-signin all along, but missing from
   * this interface — so nothing in the app could read it, which is part of why
   * the flag went unused for so long.
   */
  emailVerified?: boolean;
  photoURL?: string;
  /** The most senior role held. Derived server-side; read-only here. */
  role?: UserRole;
  /** Every role held. A person may sit in more than one team. */
  roles?: string[];
  bio?: string;
  profession?: string;
  website?: string;
  socialLinks?: {
    twitter?: string;
    linkedin?: string;
  };
  interests?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  lastActive?: Date;
  learningStats?: any;
  certifications?: UserCertification[];
  onboardingCompleted?: boolean;
  adFreeGroups?: Record<string, boolean>;
  suspended?: boolean;
  isSubscribed?: boolean; // Added
  /**
   * How many courses this account is enrolled in. Returned by `GET /users` as
   * one aggregate over the page, so it is absent on every other user payload —
   * which is why it renders as an em dash rather than a fabricated 0.
   */
  enrolmentCount?: number;
}

export interface UserLearningProgressData {
  coursesCompleted: number;
  coursesInProgress: number;
  quizzesTaken: number;
  averageQuizScore: number;
  studyHours: number;
  streakDays: number;
  lastStudySession?: Date;
  courses?: Record<string, {
    progress: number;
    lastAccessed: Date;
    completedLessons: string[];
    quizScores: Record<string, number>;
  }>;
  quizzes?: Record<string, {
    bestScore: number;
    attempts: number;
    lastAttempt: Date;
    passed: boolean;
  }>;
}

export interface UserCertification {
  id: string;
  courseId: string;
  courseName: string;
  issuedDate: Date;
  certificateUrl: string;
}

export interface UserAchievement {
  id: string;
  name: string;
  description: string;
  icon?: string;
  earnedAt: Date;
  type: "course" | "quiz" | "contribution" | "engagement";
}

export interface UserActivity {
  _id: string;
  userId: string;
  userDisplayName?: string;
  userAvatar?: string;
  type: ActivityType;
  timestamp: Date;
  metadata: {
    description?: string;
    courseId?: string;
    courseName?: string;
    quizId?: string;
    score?: number;
    postId?: string;
    title?: string;
    studyMinutes?: number;
    paymentReference?: string;
    passed?: boolean;
    preferencesUpdated?: string[];
    enrollmentType?: string;
    link?: string; // Added
    scholarshipId?: string;
    scholarshipTitle?: string;
    amount?: number;
    currency?: string;
  };
}

export type ActivityType =
  | "login"
  | "course_enrolled"
  | "course_completed"
  | "quiz_attempt"
  | "quiz_passed"
  | "post_created"
  | "post_updated"
  | "comment_added"
  | "material_uploaded"
  | "profile_updated"
  | "interests_updated"
  | "study_session_recorded"
  /* Recorded server-side, in the request that performs the action — see
     services/activityLog.ts. The four above them that nothing ever wrote
     (post_created, post_updated, comment_added) are now written too;
     material_uploaded stays declared but unwritten because the platform has
     no material-upload endpoint to record. */
  | "scholarship_applied"
  | "certificate_issued"
  | "payment_completed";

export interface UserPermissions {
  canCreateCourses: boolean;
  canCreateQuizzes: boolean;
  canUploadMaterials: boolean;
  canManageUsers: boolean;
  canModerateContent: boolean;
  canAccessAdminPanel: boolean;
  canAccessAnalytics: boolean;
  canEditProfile: boolean;
  canDeleteAccount: boolean;
  canViewAnalytics: boolean;
  canCreateContent: boolean;
  canManageContent: boolean;
  canApproveContent: boolean;
}

export interface Instructor {
  _id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  photoFile?: File;
  about?: string;
  education?: {
    degree: string;
    institution: string;
    year: number;
  }[];
  workExperience?: {
    title: string;
    company: string;
    years: string;
    description?: string;
  }[];
  socialLinks?: {
    twitter?: string;
    linkedin?: string;
    website?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  rating?: number;
}

export interface Speaker {
  name: string;
  photo?: string;
  credentials?: string;
}

export interface Post {
  _id: string;
  title: string;
  content: string;
  excerpt: string;
  coverImage?: File | string | null;
  authorId: string;
  createdAt: Date;
  updatedAt: Date;
  published: boolean;
  publishedAt?: Date;
  /** Set only while `status` is `scheduled`; the publish tick clears it. */
  scheduledFor?: string;
  views: number;
  likes: number;
  dislikes: number;
  commentsCount: number;
  /**
   * `archived` never existed on the server — the schema's enum was
   * `draft | published`, so nothing could ever hold that value and any UI
   * branching on it was dead. `scheduled` is real and replaces it.
   */
  status: "draft" | "scheduled" | "published";
  tags: string[];
  category: string;
  /**
   * Flat, matching what the API accepts and returns. The nested `seoMetadata`
   * object this replaced had no counterpart on the server: an editor's SEO
   * fields were posted under a key the controller never read.
   */
  seoTitle?: string;
  seoDescription?: string;
  timeToRead?: number;
  slug?: string;
  /** Present on every response — the controller spreads `{ id, ...post }`. */
  id?: string;
}

export interface PostFormData {
  title: string;
  content: string;
  excerpt: string;
  coverImage?: File | string | null;
  published: boolean;
  tags: string[];
  category: string;
}

export interface PostWithAuthor extends Post {
  author: {
    name: string;
    avatar?: string;
    [key: string]: any; // For other author details
  };
}

export interface Comment {
  _id: string;
  postId: string;
  parentId?: string | null;
  userId: string;
  userName?: string;
  userAvatar?: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  likes: number;
  dislikes: number;
  edited: boolean;
  userLiked?: boolean;
  userDisliked?: boolean;
}

export interface CommentFormData {
  postId: string;
  parentId?: string;
  content: string;
}

export interface Course {
  _id: string;
  title: string;
  description: string;
  authorId: string;
  instructorId: string; // Changed to instructorId
  instructorName?: string; // New field
  instructorProfile?: Instructor; // New field
  authorAvatar?: string;
  price?: number; // General price field
  price_ngn?: number;
  price_usd?: number;
  promoPrice?: number; // Added for explicit promo price
  currency?: string;
  // Present on detail responses only. List endpoints omit it and send the counts
  // below instead — see COURSE_SUMMARY_PROJECTION in backend/src/controllers/course.ts.
  modules?: CourseModule[];
  moduleCount?: number;
  lessonCount?: number;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  status: "draft" | "published" | "archived";
  enrolledStudents: number;
  rating: number;
  ratingCount: number;
  featured: boolean;
  certificateAvailable: boolean;
  categories: string[];
  tags: string[];
  seoMetadata?: {
    title?: string;
    description?: string;
    keywords?: string;
  };
  slug?: string;
  level?: "beginner" | "intermediate" | "advanced";
  // Minutes, matching the backend. This said "in hours" while the API has always
  // sent minutes, which is how three render sites ended up printing "750 hours".
  // Format it with `describeCourseTiming`/`formatDuration` from lib/courseTiming.
  duration?: number;
  discount?: number; // percentage
  coverImage?: string;
  /**
   * Set by the detail endpoints when the caller is not enrolled: the curriculum
   * outline is present but lesson bodies and quizzes have been stripped server
   * side. Treat it as "this viewer may not open the player".
   */
  locked?: boolean;
}

export enum CourseStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  ARCHIVED = "archived",
}

export interface CourseFormData {
  title: string;
  description: string;
  instructorId: string; // Changed to instructorId
  authorAvatar?: string;
  price?: number;
  price_ngn?: number;
  price_usd?: number;
  promoPrice?: number; // Added for explicit promo price
  currency?: string;
  modules: CourseModule[];
  published: boolean;
  featured?: boolean;
  certificateAvailable?: boolean;
  categories: string[];
  tags: string[];
  slug?: string;
  level?: "beginner" | "intermediate" | "advanced";
  duration?: number;
  discount?: number;
}

export interface CourseModule {
  id: string;
  title: string;
  description?: string; // Added
  lessons: CourseLesson[];
  quiz?: Quiz;
}

export interface CourseLesson {
  id: string;
  title: string;
  type: "markdown" | "video" | "quiz" | "assignment" | "code";
  content?: string;
  videoUrl?: string;
  /**
   * MINUTES, matching `ILesson.duration` on the server — never hours. Populated
   * by recalibrateCourses.ts for the whole catalogue. Its absence here was why
   * `describeCourseTiming(course)` would not typecheck against `CourseModule`,
   * and why the detail page fell back to an average sitting length while the
   * real per-lesson minutes sat unread in the response.
   */
  duration?: number;
  quiz?: Quiz;
  assignment?: Assignment;
  codeSnippets?: CodeSnippet[];
  isFree?: boolean; // For previewing paid courses
  description?: string;
  explanation?: string; // Added
  order?: number; // Added
}

export interface CodeSnippet {
  id: string;
  language: string;
  code: string;
  title?: string;
}

export interface Quiz {
  id: string;
  title: string;
  description?: string;
  courseId?: string;
  instructorId?: string;
  questions: QuizQuestion[];
  duration?: number;
  status?: "draft" | "published" | "archived";
  createdAt?: Date;
  updatedAt?: Date;
  publishedAt?: Date;
  passingScore: number;
  showCorrectAnswers: boolean;
  showExplanations: boolean;
  maxAttempts: number;
  lessonId?: string;
  moduleId?: string;
  tags?: string[];
  type?: "multiple-choice" | "true-false" | "fill-in-the-blank";
}

export interface QuizFormData {
  title: string;
  questions: QuizQuestion[];
  passingScore: number;
  showCorrectAnswers: boolean;
  showExplanations: boolean;
  maxAttempts: number;
  lessonId?: string;
  moduleId?: string;
  courseId?: string;
  published?: boolean;
  tags?: string[];
  type?: "multiple-choice" | "true-false" | "fill-in-the-blank";
}

export interface QuizQuestion {
  id: string;
  question: string;
  /**
   * `isCorrect` and `correctAnswers` only ever arrive on an authoring screen.
   * The course body a learner receives has them stripped server-side — the key
   * used to ship with the questions, which put it in the network tab before
   * anyone had answered anything. The player gets it back in `QuizReviewEntry`
   * after grading.
   */
  options: { id: string; text: string; isCorrect?: boolean }[];
  correctAnswers?: string[]; // IDs of correct options
  questionType: "single-choice" | "multiple-choice" | "true-false"; // Renamed type to questionType, added true-false
  correctAnswerId?: string; // Added for form handling
  points: number;
  explanation?: string;
}

export interface QuizAttempt {
  id: string;
  quizId: string;
  userId: string;
  courseId?: string;
  lessonId?: string;
  score: number;
  totalPossibleScore: number;
  percentageScore: number;
  passed: boolean;
  /**
   * The pass mark the grader actually applied, as a percentage. Not the same as
   * the `passingScore` stored on the quiz — that one is a raw question count as
   * often as it is a percentage, so only this normalised figure is safe to show
   * a learner. Absent on attempts loaded from history rather than just graded.
   */
  passingScore?: number;
  answers: QuizAnswer[];
  /** The answer key, returned by the grader once the paper is in. */
  review?: QuizReviewEntry[];
  startedAt?: Date;
  completedAt?: Date;
  timeSpent?: number; // in seconds
}

/** One question's answer key, as the grading response returns it. */
export interface QuizReviewEntry {
  questionId: string;
  correctAnswer?: string;
  correctAnswers?: string[];
  explanation?: string;
}

export interface QuizAnswer {
  questionId: string;
  selectedAnswer: string | string[];
  isCorrect: boolean;
  pointsEarned: number;
}

export interface Assignment {
  id: string;
  title: string;
  description: string;
  instructions: string;
  submissionType: "code" | "text" | "file" | "url";
}

export interface AssignmentSubmission {
  id: string;
  assignmentId: string;
  lessonId: string;
  courseId: string;
  userId: string;
  submissionContent: string | File; // URL for file/url types
  submittedAt: Date;
  status: "pending" | "graded" | "resubmit";
  grade?: number;
  feedback?: string;
}

export interface CourseEnrollment {
  _id: string;
  userId: string;
  courseId: string | Course; // Can be string ID or populated Course object
  enrolledAt?: Date;
  updatedAt?: Date;
  progress: number; // 0-100
  completedLessons: { lessonId: string; completedAt: Date }[];
  status: CourseEnrollmentStatus;
  certificateIssued: boolean;
  certificateUrl?: string;
  hasPaid?: boolean;
  paymentReference?: string;
  enrollmentType?: "paid" | "scholarship" | "free";
  completedAt?: Date;
}

export enum CourseEnrollmentStatus {
  IN_PROGRESS = "in-progress",
  COMPLETED = "completed",
  PAUSED = "paused",
  DROPPED = "dropped",
}

export interface Scholarship {
  _id: string;
  title: string;
  description: string;
  organizationName: string;
  organizationLogo?: string;
  amount: number;
  currency: string;
  eligibilityCriteria: string;
  applicationProcess: string;
  requiredDocuments: string[];
  applicationDeadline: Date;
  startDate: Date;
  endDate?: Date;
  maxRecipients?: number;
  status: "draft" | "open" | "closed" | "archived";
  location?: {
    country?: string;
    state?: string;
    city?: string;
  };
  tags?: string[];
  categories?: string[];
  contact?: {
    email?: string;
    phone?: string;
    website?: string;
  };
  createdBy: string;
  createdAt: Date;
  updatedAt?: Date;
  publishedAt?: Date;
  views: number;
  currentRecipients: number;
  uniqueLink?: string;
  associatedCourseId?: string;
  associatedCourseName?: string;
  featured?: boolean;
  type?: ScholarshipType; // Changed to use ScholarshipType
  coverImage?: string; // Added
}

export type ScholarshipType = "full" | "partial" | "merit-based" | "need-based" | "research" | "specific-group" | "other";

export interface ScholarshipFormData {
  title: string;
  description: string;
  organizationName: string;
  organizationLogoFile?: File | null;
  existingOrganizationLogoUrl?: string;
  amount: number;
  currency: string;
  eligibilityCriteria: string;
  applicationProcess: string;
  requiredDocuments: string;
  applicationDeadline: Date;
  startDate: Date;
  endDate?: Date | null;
  maxRecipients?: number;
  status: "draft" | "open" | "closed" | "archived";
  locationCountry?: string;
  locationState?: string;
  locationCity?: string;
  tags?: string;
  categories?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactWebsite?: string;
  coverImageFile?: File | null;
  existingCoverImageUrl?: string;
  associatedCourseId?: string;
  featured?: boolean;
}

export type ScholarshipStatus = "draft" | "open" | "closed" | "archived";

export interface ScholarshipApplication {
  _id: string;
  scholarshipId: string;
  userId: string;
  userName: string;
  userEmail: string;
  applicationDate: Date;
  status: "pending" | "approved" | "rejected";
  applicationDetails: Record<string, any>;
  rejectionReason?: string;
  adminNotes?: string;
  /**
   * Whether the applicant has actually been told — a different question from
   * whether it has been decided, and the one the reviewer cannot otherwise
   * answer. Thirty-one approvals sat unannounced because the panel showed a
   * status and nothing about whether it had reached anyone.
   */
  decisionEmailedAt?: string;
  decisionEmailStatus?: 'sent' | 'skipped' | 'failed';
  decisionEmailNote?: string;
  timestamp?: number; // Unix timestamp for RTDB ordering
}

export interface Testimonial {
  _id: string;
  authorName: string;
  authorRole: string;
  text: string;
  avatar?: string;
  order?: number;
  rating: number;
}

export interface RandomFact {
  id: number;
  text: string;
  category: string;
  icon: JSX.Element;
}

export interface StudyMaterial {
  _id: string;
  title: string;
  description: string;
  materialType: "document" | "video" | "audio";
  format: string; // e.g., PDF, MP4
  url: string;
  tags: string[];
  category: string;
  authorId: string;
  createdAt: Date;
  updatedAt: Date;
  published: boolean;
  views: number;
  downloads: number;
  status: "draft" | "published" | "archived";
  courseId?: string;
  lessonId?: string;
  school?: string;
  department?: string;
  level?: string;
  semester?: string;
  year?: number;
  contributorName?: string;
  contributorEmail?: string;
}

export interface PastQuestion extends StudyMaterial {
  examType: string; // e.g., Midterm, Final
  courseCode: string;
  courseName: string;
  year: number;
  solutionUrl?: string;
}

export interface AdminUserView {
  _id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  userRole: UserRole; // Changed from 'role' to 'userRole'
  createdAt: string;
  lastActive: string;
  isSuspended: boolean; // Changed from 'isActive' to 'isSuspended'
  isSubscribed: boolean;
}

export interface Notification {
  _id: string;
  recipientId: string;
  senderId?: string;
  senderName?: string;
  senderAvatar?: string;
  type: NotificationType;
  content: string;
  postId?: string;
  postTitle?: string;
  commentId?: string;
  actionLink?: string;
  read: boolean;
  createdAt: Date;
}

export type NotificationType =
  | "comment"
  | "reply"
  | "like"
  | "system"
  | "scholarship_application"
  | "course_enrollment";

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  emailNotifications: boolean;
  pushNotifications: boolean;
  newsletterSubscription: boolean;
  contentLanguage?: string; // Added
  timezone?: string; // Added
  privacySettings: {
    showProfile: boolean;
    showActivity: boolean;
    showAchievements: boolean;
    allowMessages: boolean;
  };
}

export interface CertificateRequestData {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  courseId: string;
  courseName: string;
  /** Progress snapshot taken when the learner asked, shown to the reviewing admin. */
  completionPercentage?: number;
  status: "pending" | "rejected" | "issued";
  requestedAt: Date;
  issuedAt?: Date;
  reviewedAt?: Date;
  certificateId?: string;
  certificateUrl?: string;
  rejectionReason?: string;
}

export interface CertificateData {
  _id: string;
  certificateNumber: string;
  userId: string;
  userName: string;
  userEmail: string;
  courseId: string;
  courseTitle: string;
  issueDate: string;
  certificateUrl: string;
  pdfUrl?: string;
  revoked: boolean;
  revokedAt?: string;
  revokedReason?: string;
}

/**
 * Public verification result. Carries no PII beyond the holder's name and course.
 *
 * The two arms are named rather than left anonymous because this project compiles
 * with `strict: false`: without `strictNullChecks` TypeScript will not narrow a
 * boolean-literal discriminant, so consumers have to name the arm they want.
 */
export interface CertificateVerificationValid {
  valid: true;
  certificateNumber: string;
  holderName: string;
  courseTitle: string;
  issueDate: string;
  issuer: string;
}

export interface CertificateVerificationFailure {
  valid: false;
  reason: "not_found" | "revoked" | "rate_limited" | "error";
  certificateNumber?: string;
  holderName?: string;
  courseTitle?: string;
  issueDate?: string;
  revokedAt?: string;
}

export type CertificateVerification = CertificateVerificationValid | CertificateVerificationFailure;

export interface UserCourseProgress {
  userId: string;
  courseId: string;
  startedAt: Date;
  lastAccessedAt: Date;
  completedLessons: { lessonId: string; completedAt: Date }[];
  quizResults: QuizResult[];
  assignmentSubmissions: AssignmentSubmission[];
  completionPercentage: number;
  certificateIssued: boolean;
  certificateUrl?: string;
  hasPaid?: boolean;
  paymentReference?: string;
  /** Where the learner was. Exactly one of these is set at a time. */
  currentLessonId?: string;
  currentQuizId?: string;
  /**
   * Derived server-side and returned with the progress record, so the player
   * does not have to reconstruct the sequence to know what is unlocked.
   */
  frontierIndex?: number;
  resumeItem?: ProgressionResumeItem | null;
  totalItems?: number;
  isComplete?: boolean;
}

export type ProgressionItemType = 'lesson' | 'quiz';

export interface ProgressionResumeItem {
  itemId: string;
  itemType: ProgressionItemType;
  moduleId: string;
  index: number;
}

export interface QuizResult {
  quizId: string;
  userId: string;
  courseId: string;
  score: number;
  totalPossibleScore: number;
  percentageScore?: number;
  /** The server's verdict. Never computed in the browser — see Quiz.tsx. */
  passed: boolean;
  completedAt: Date;
  answers: QuizAnswer[];
}

export interface CartItem {
  courseId: string;
  quantity: number;
  price: number;
  name: string;
  image?: string;
}

export interface Order {
  id: string;
  userId: string;
  items: CartItem[];
  totalAmount: number;
  currency: string;
  paymentStatus: "pending" | "completed" | "failed";
  paymentReference?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaymentDetails {
  userId: string;
  courseId: string;
  amountPaid: number;
  paymentReference: string;
  currency: string;
  timestamp: Date;
}

export interface Event {
  _id: string;
  title: string;
  description: string;
  start: Date;
  end: Date;
  location: string;
  organizerId: string;
  organizerName?: string;
  createdAt: Date;
  updatedAt: Date;
  bannerImage?: string;
  date: Date;
  registrationLink?: string | null;
  agenda?: string | null;
  slug?: string;
  status: "upcoming" | "live" | "past" | "cancelled";
  type: "webinar" | "workshop" | "conference" | "meetup";
  resources?: { name: string; url: string; type: "document" | "video" | "link" }[]; // Added
  recordingLink?: string; // Added
}

export interface AchievementCriteria extends UserAchievement {
  check: () => boolean;
  type: "course" | "quiz" | "contribution" | "engagement";
}

export interface StudyMaterialFilters {
  category?: string;
  type?: "document" | "video" | "audio";
  schoolId?: string;
  courseCode?: string;
  courseTitle?: string;
  level?: string;
  session?: string;
  search?: string;
  materialType?: "document" | "pastQuestion";
}

export interface PromoCode {
  id: string;
  code: string;
  percentageOffset: number; // e.g., 0.10 for 10%
  isActive: boolean;
  /**
   * Why this code exists: the campaign that prints it, the channel it runs on
   * and the month it belongs to. Optional, and older codes have none — but
   * without it an unfamiliar code cannot be retired, because nobody can tell
   * whether an advert is still quoting it.
   */
  reason?: string;
  createdAt: Date;
  updatedAt: Date;
}


export interface Team {
  _id?: string;
  id?: string;
  name: string;
  role: string;
  imageUrl?: string;
  about?: string; // Added
  socialLinks?: { // Added
    twitter?: string;
    linkedin?: string;
    website?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface TeamsResponse {
  teams: Team[];
  totalPages: number;
}

export interface FetchPostsParams {
  limit?: number;
  page?: number;
  status?: string;
  category?: string;
  tag?: string;
  authorId?: string;
  search?: string;
  featured?: boolean;
}